import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "../api";
import { useAuth } from "../contexts/AuthContext";
import useTakofyMeta from "../hooks/useTakofyMeta";
import { getAuthErrorMessage, getPostAuthRoute } from "../utils/authRouting";
import "./PredictionAuth.css";

const copy = {
  login: { kicker: "TAKOFY LİG", title: "Tahmin ligine giriş yap", subtitle: "Tahminlerini kaydetmek ve sıralamadaki yerini görmek için devam et." },
  register: { kicker: "TAKOFY LİG", title: "Ligde yerini al", subtitle: "Hesabını oluştur, Süper Lig skor tahminlerine hemen başla." },
  forgot: { kicker: "TAKOFY LİG", title: "Şifreni yenile", subtitle: "Hesabına bağlı e-posta adresini gir; sana güvenli bir sıfırlama bağlantısı gönderelim." },
  reset: { kicker: "TAKOFY LİG", title: "Yeni şifreni belirle", subtitle: "Güçlü bir şifre seçerek tahmin ligine geri dön." },
};

export default function PredictionAuth({ mode = "login" }) {
  useTakofyMeta({ title: "Takofy Hesap İşlemleri", description: "Takofy kullanıcı hesabı giriş ve kayıt işlemleri.", robots: "noindex, nofollow, noarchive" });
  const navigate = useNavigate();
  const location = useLocation();
  const { markLoggedIn, refreshAuth } = useAuth();
  const [form, setForm] = useState(() => {
    let draft = {};
    try { draft = JSON.parse(sessionStorage.getItem(`takofy_auth_draft_${mode}`) || "{}"); } catch {}
    return { username: draft.username || "", email: draft.email || "", password: "", confirm: "", remember: true, legalAccepted: false };
  });
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [message, setMessage] = useState(location.state?.resetSuccess ? "Şifren güncellendi. Yeni şifrenle giriş yapabilirsin." : location.state?.signedOut ? "Güvenli biçimde çıkış yaptın." : location.state?.sessionExpired ? "Oturum süren doldu. Devam etmek için yeniden giriş yap." : "");
  const [loading, setLoading] = useState(false);
  const resetToken = useMemo(() => new URLSearchParams(location.search).get("token") || "", [location.search]);
  const text = copy[mode] || copy.login;

  useEffect(() => {
    if (!["login", "register", "forgot"].includes(mode)) return;
    sessionStorage.setItem(`takofy_auth_draft_${mode}`, JSON.stringify({ username: form.username, email: form.email }));
  }, [form.email, form.username, mode]);

  const update = (event) => {
    const { name, value, checked, type } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    setFieldErrors((current) => ({ ...current, [name]: "" }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setFieldErrors({});
    setMessage("");

    try {
      setLoading(true);
      if (mode === "login") {
        const nextErrors = { username: !form.username ? "Kullanıcı adı veya e-posta zorunludur." : "", password: !form.password ? "Şifre zorunludur." : "" };
        if (nextErrors.username || nextErrors.password) { setFieldErrors(nextErrors); return; }
        const response = await api.post("/auth/login", { username: form.username, password: form.password, remember: form.remember });
        markLoggedIn(response?.data?.user || null);
        await refreshAuth(true);
        sessionStorage.removeItem("takofy_auth_draft_login");
        navigate(getPostAuthRoute(response?.data?.user, location.state?.from || "/"), { replace: true });
        return;
      }
      if (mode === "register") {
        const nextErrors = { username: !form.username ? "Kullanıcı adı zorunludur." : "", email: !form.email ? "E-posta zorunludur." : "", password: !form.password ? "Şifre zorunludur." : "", confirm: !form.confirm ? "Şifre tekrarı zorunludur." : "" };
        if (Object.values(nextErrors).some(Boolean)) { setFieldErrors(nextErrors); return; }
        if (!/^[\p{L}\p{N}._-]+$/u.test(form.username.trim())) throw new Error("Kullanıcı adı yalnızca harf, rakam, nokta, tire ve alt çizgi içerebilir.");
        if (form.password.length < 6) throw new Error("Şifre en az 6 karakter olmalı.");
        if (form.password !== form.confirm) throw new Error("Şifreler eşleşmiyor.");
        if (!form.legalAccepted) throw new Error("Kurallar ve gizlilik metinlerini kabul etmelisin.");
        const response = await api.post("/auth/register", { username: form.username, email: form.email, password: form.password });
        markLoggedIn(response?.data?.user || null);
        await refreshAuth(true);
        sessionStorage.removeItem("takofy_auth_draft_register");
        navigate(getPostAuthRoute(response?.data?.user, location.state?.from || "/"), { replace: true });
        return;
      }
      if (mode === "forgot") {
        if (!form.username) throw new Error("E-posta veya kullanıcı adı zorunludur.");
        await api.post("/auth/forgot-password", { username: form.username });
        setMessage("Uygun bir hesap varsa şifre sıfırlama bağlantısı e-posta adresine gönderildi.");
        return;
      }
      if (!resetToken) throw new Error("Şifre sıfırlama bağlantısı geçersiz.");
      if (form.password.length < 6) throw new Error("Şifre en az 6 karakter olmalı.");
      if (form.password !== form.confirm) throw new Error("Şifreler eşleşmiyor.");
      await api.post("/auth/reset-password", { token: resetToken, password: form.password });
      navigate("/login", { replace: true, state: { resetSuccess: true } });
    } catch (err) {
      setError(getAuthErrorMessage(err, err.message || "İşlem tamamlanamadı."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="prediction-auth">
      <Link className="prediction-auth__brand" to="/">TAKOFY <span>LİG</span></Link>
      <section className="prediction-auth__card">
        <p className="prediction-auth__kicker">{text.kicker}</p>
        <h1>{text.title}</h1>
        <p className="prediction-auth__intro">{text.subtitle}</p>
        {error ? <div className="prediction-auth__alert">{error}</div> : null}
        {message ? <div className="prediction-auth__alert prediction-auth__alert--success">{message}</div> : null}
        <form onSubmit={submit} className="prediction-auth__form">
          {mode !== "reset" ? <label>{mode === "register" ? "Kullanıcı adı" : "Kullanıcı adı veya e-posta"}<input name="username" value={form.username} onChange={update} autoComplete="username" disabled={loading} aria-invalid={!!fieldErrors.username} />{fieldErrors.username?<small className="prediction-auth__field-error">{fieldErrors.username}</small>:mode==="register"?<small>3–120 karakter · harf, rakam, nokta, tire veya alt çizgi</small>:null}</label> : null}
          {mode === "register" ? <label>E-posta adresi<input type="email" inputMode="email" name="email" value={form.email} onChange={update} autoComplete="email" disabled={loading} aria-invalid={!!fieldErrors.email}/>{fieldErrors.email?<small className="prediction-auth__field-error">{fieldErrors.email}</small>:null}</label> : null}
          {mode !== "forgot" ? <label>{mode === "reset" ? "Yeni şifre" : "Şifre"}<input type="password" name="password" value={form.password} onChange={update} autoComplete={mode === "login" ? "current-password" : "new-password"} disabled={loading} aria-invalid={!!fieldErrors.password}/>{fieldErrors.password?<small className="prediction-auth__field-error">{fieldErrors.password}</small>:null}</label> : null}
          {(mode === "register" || mode === "reset") ? <label>Şifre tekrar<input type="password" name="confirm" value={form.confirm} onChange={update} autoComplete="new-password" disabled={loading} aria-invalid={!!fieldErrors.confirm}/>{fieldErrors.confirm?<small className="prediction-auth__field-error">{fieldErrors.confirm}</small>:null}</label> : null}
          {mode === "register" ? <label className="prediction-auth__legal"><input type="checkbox" name="legalAccepted" checked={form.legalAccepted} onChange={update}/><span><Link to="/kurallar" target="_blank">Oyun kurallarını</Link>, <Link to="/privacy-policy" target="_blank">Gizlilik Politikası'nı</Link> ve <Link to="/kvkk" target="_blank">KVKK metnini</Link> okudum, kabul ediyorum.</span></label> : null}
          {mode === "login" ? <div className="prediction-auth__row"><label className="prediction-auth__remember"><input type="checkbox" name="remember" checked={form.remember} onChange={update} /> Beni hatırla</label><Link to="/forgot">Şifremi unuttum</Link></div> : null}
          <button disabled={loading}>{loading ? "İşleniyor..." : mode === "login" ? "Giriş yap" : mode === "register" ? "Hesap oluştur" : mode === "forgot" ? "Sıfırlama bağlantısı gönder" : "Şifreyi güncelle"}</button>
        </form>
        <p className="prediction-auth__foot">
          {mode === "login" ? <>Hesabın yok mu? <Link to="/register">Kayıt ol</Link></> : <><Link to="/login">Giriş ekranına dön</Link></>}
        </p>
      </section>
    </main>
  );
}
